import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getBatchProviderAbsences } from '../services/absences/providerAbsenceService';
import { ProviderAbsence } from '../types/absences/types';

interface UseBatchAbsencesProps {
  providerIds: string[];
  practiceIds: string[];
  startDate?: string;
  endDate?: string;
}

interface UseBatchAbsencesReturn {
  absencesByProvider: Record<string, ProviderAbsence[]>;
  loading: boolean;
  error: string | null;
  refreshAbsences: () => Promise<void>;
}

export const useBatchAbsences = ({
  providerIds,
  practiceIds,
  startDate,
  endDate,
}: UseBatchAbsencesProps): UseBatchAbsencesReturn => {
  const { accessToken, isAuthenticated } = useAuth();
  const [absencesByProvider, setAbsencesByProvider] = useState<
    Record<string, ProviderAbsence[]>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  const lastProviderIds = useRef<string[]>([]);
  const lastPracticeIds = useRef<string[]>([]);

  const fetchBatchAbsences = useCallback(async () => {
    if (!providerIds.length || !practiceIds.length) {
      return;
    }

    if (!isAuthenticated || !accessToken) {
      return;
    }

    // Check if we've already fetched for these exact IDs
    const providerIdsChanged =
      JSON.stringify(providerIds) !==
      JSON.stringify(lastProviderIds.current);
    const practiceIdsChanged =
      JSON.stringify(practiceIds) !==
      JSON.stringify(lastPracticeIds.current);

    if (
      hasFetched.current &&
      !providerIdsChanged &&
      !practiceIdsChanged
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getBatchProviderAbsences(
        providerIds,
        practiceIds,
        startDate,
        endDate
      );

      // Group absences by provider ID
      const groupedAbsences: Record<string, ProviderAbsence[]> = {};

      // Initialize all providers with empty arrays
      providerIds.forEach((providerId) => {
        groupedAbsences[providerId] = [];
      });

      // Group the returned absences by provider
      if (response.absences) {
        response.absences.forEach((absence) => {
          // We need to check if the absence has a providerId field or if we need to infer it
          // For now, assuming the API returns absences with providerId
          if (
            absence.providerId &&
            groupedAbsences[absence.providerId]
          ) {
            groupedAbsences[absence.providerId].push(absence);
          } else {
            // Add a special key for unknown/missing providerId
            if (!groupedAbsences['__unknown__']) {
              groupedAbsences['__unknown__'] = [];
            }
            console.warn(
              '[useBatchAbsences] Absence missing providerId or providerId not in providerIds. Grouping under "__unknown__":',
              absence
            );
            groupedAbsences['__unknown__'].push(absence);
          }
        });
      }

      setAbsencesByProvider(groupedAbsences);

      // Mark as fetched and store the IDs
      hasFetched.current = true;
      lastProviderIds.current = [...providerIds];
      lastPracticeIds.current = [...practiceIds];
    } catch (err) {
      console.error('Failed to fetch batch absences:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch absences'
      );

      // Initialize with empty arrays on error
      const emptyAbsences: Record<string, ProviderAbsence[]> = {};
      providerIds.forEach((providerId) => {
        emptyAbsences[providerId] = [];
      });
      setAbsencesByProvider(emptyAbsences);
    } finally {
      setLoading(false);
    }
  }, [providerIds, practiceIds, startDate, endDate, accessToken, isAuthenticated]);

  const refreshAbsences = useCallback(async () => {
    hasFetched.current = false;
    await fetchBatchAbsences();
  }, [fetchBatchAbsences]);

  useEffect(() => {
    if (isAuthenticated && accessToken) {
      fetchBatchAbsences();
    }
  }, [fetchBatchAbsences, accessToken, isAuthenticated]);

  return {
    absencesByProvider,
    loading,
    error,
    refreshAbsences,
  };
};
